import React, { useEffect, useRef, useState } from "react";
import {
  of,
  Subject,
  catchError,
  debounceTime,
  filter,
  tap,
  map,
  switchMap,
  takeUntil,
} from "rxjs";
import PropTypes from "prop-types";
import Autocomplete from "../atoms/Autocomplete";
import apiClient from "../../services/apiClient";

function HPOSelector({ hpo, addItem, source }) {
  const search$ = useRef(new Subject()).current;
  const inputTxtRef = useRef();
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    const destroy$ = new Subject();
    const phenIds = (hpo.diseases || []).map((x) => x.id);
    const disIds = (hpo.phenotypes || []).map((x) => x.id);
    const idSet = new Set([...phenIds, ...disIds]);

    search$
      .pipe(
        debounceTime(200),
        filter((query) => query.length > 2),
        map((query) => ({ query, phenotypes_sources: source })),
        switchMap((x) => apiClient.getPhenSuggestions(x)),
        map((x) => x.filter((y) => !idSet.has(y.id))),
        tap((x) => setSuggestions(x)),
        catchError((e) => {
          console.error(e);
          return of([]);
        }),
        takeUntil(destroy$)
      )
      .subscribe();

    return () => destroy$.next();
  }, [source, hpo.phenotypes, hpo.diseases]);

  useEffect(() => {
    setSuggestions([]);
  }, [source]);

  const handleClick = (item) => {
    setSuggestions(suggestions.filter((x) => x.id !== item.id));

    of(null)
      .pipe(
        switchMap(() => apiClient.getDiseaseWithPhenotypes(item.id)),
        catchError(() => of({ phenotype_name: item.name })),
        map((x) => ({ ...item, ...x })),
        tap(addItem)
      )
      .subscribe();
  };

  return (
    <div className="tw-max-w-screen-sm">
      <Autocomplete
        optionKey="id"
        options={suggestions}
        onSelect={(x) => handleClick(x)}
        OptionContent={({ option, isHighlighted }) => {
          const cl = "tw-px-2 tw-py-1 tw-cursor-pointer";
          const highlightCl = isHighlighted ? "tw-bg-blueGray-200" : "";
          return <div className={`${cl} ${highlightCl}`}>{option.name}</div>;
        }}
        OptionsWrapper={({ children }) => (
          <div className="tw-max-h-60 tw-bg-white tw-overflow-auto tw-mt-2 rounded-lg tw-shadow-lg tw-border border-1">
            {children}
          </div>
        )}
      >
        <input
          id="phenotype-selector"
          ref={inputTxtRef}
          onChange={(e) => search$.next(e.target.value)}
          placeholder="Search for phenotypes..."
          type="text"
          autoComplete="off"
          className="focus:tw-border-primary tw-w-full tw-px-1 tw-py-1 tw-border tw-border-blueGray-300 tw-outline-none"
        />
      </Autocomplete>
    </div>
  );
}

HPOSelector.propTypes = {
  hpo: PropTypes.shape({
    diseases: PropTypes.arrayOf(PropTypes.shape({})),
    phenotypes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  addItem: PropTypes.func.isRequired,
  source: PropTypes.string,
};

HPOSelector.defaultProps = {
  hpo: { diseases: [], phenotypes: [] },
  source: "all",
};

export default HPOSelector;
